import React, { useEffect, useState } from "react";
import { useParams, Prompt } from "react-router-dom";
import { Box, Divider, Grid, TextField, Backdrop } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  postVehicleRegistrationNumbersAction,
  postGateOutAction,
} from "src/app-redux/gate-entry/actions/gateEntryActions";
import {
  checkOutDetail,
  checkoutGateEntryDetail,
  emptyCheckoutGateEntryDetail,
  emptyCheckoutObj,
} from "src/app-redux/gate-entry/gateEntrySlice";
import { useAppDispatch, useAppSelector } from "src/app-redux/hooks";
import { addNotificationAction } from "src/app-redux/settings/settingsSlice";
import { IVehicleNumber } from "src/common/typings/vehicle";
import CommonAutoCompleteField from "src/components/CommonAutoCompleteField/CommonAutoCompleteField";
import CommonButton from "src/components/CommonButton/CommonButton";
import { useStyles } from "./style";
import { StatusCompleted, StatusPending, StatusCancel } from "./typing";
import { fetchWrapper } from "src/api-client/fetchWrapper";
import TransitionDialog from "src/components/AlertDialog/TransitionDialog";
import { getPrintFile } from "src/app-redux/common-actions";
import moment from "moment";
import DuplicateReceiptDialog from "src/components/AlertDialog/DuplicateReceiptDialog";
import CommonLoader from "src/components/Loader/CommonLoader";

type Props = {
  vehicleNumbers: IVehicleNumber[];
  vehicleDetails: any;
};

export default function GateOutInput({ vehicleDetails }: Props): React.ReactElement {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const [loader, setLoader] = useState<any>(false);

  // from Store
  const checkOutDetailObj: any = useAppSelector(checkOutDetail);
  const checkoutGateEntryDetailObj: any = useAppSelector(checkoutGateEntryDetail);

  const [vehicleRegNumberArr, setvehicleRegNumberArr] = React.useState<any[]>([]);
  const [fetch, setFetch] = useState<boolean>(true);
  const { id } = useParams<{ id: any }>();
  const [vehicleNumberLength, setVehicleNumberLength] = useState<number>(0);

  const validationSchema = Yup.object().shape({
    remarks: Yup.string().max(3000, "Maximum 3000 characters allowed"),
  });

  const formik = useFormik({
    initialValues: {
      registrationNumber: "",
      remarks: "",
      gatePassId: checkoutGateEntryDetailObj.gateEntryId || 0,
    },
    validationSchema: validationSchema,
    onSubmit: async (values: any, actions) => {
      if (values.gatePassId === 0 || values.gatePassId === undefined) {
        dispatch(
          addNotificationAction({
            message: "Please search Vehicle Number first.",
            options: { variant: "error" },
          })
        );
        return;
      }
      const res: any = await dispatch(postGateOutAction(values));
      if (res.payload) {
        actions.setSubmitting(false);
        actions.resetForm();
        setvehicleRegNumberArr([]);
        dispatch(emptyCheckoutObj());
        dispatch(emptyCheckoutGateEntryDetail());
        setIsDirty(false);
      }
    },
  });

  const { values, setValues } = formik;

  const getVehicleByRegistrationNo = async (queryString: string) => {
    let results: any = [];
    results = await fetchWrapper.get(
      `search-vehicles-by-vehicle-registration-number/${queryString}`
    );
    if (results.isSuccess) {
      setvehicleRegNumberArr(results.value);
    }
    return;
  };

  const handleSearch = async (regNo: any) => {
    const value = regNo;
    if (value === "" || value === undefined || value === 0) {
      dispatch(
        addNotificationAction({
          message: "Please select Vehicle Number first.",
          options: { variant: "error" },
        })
      );
    } else {
      await dispatch(postVehicleRegistrationNumbersAction(regNo));
    }
  };
  useEffect(() => {
    setValues({ ...values, gatePassId: checkOutDetailObj.gateEntryId });
    if (id && vehicleDetails) {
      setRegNo();
    }
  }, [vehicleDetails]);
  const setRegNo = async () => {
    if (vehicleDetails.vehicleRegNumber && fetch) {
      formik.setFieldValue("registrationNumber", vehicleDetails.vehicleRegNumber);
      getVehicleByRegistrationNo(vehicleDetails.vehicleRegNumber);
      dispatch(postVehicleRegistrationNumbersAction(vehicleDetails.vehicleRegNumber));
      setFetch(false);
    }
  };

  useEffect(() => {
    return () => {
      // Empty Records
      dispatch(emptyCheckoutObj());
    };
  }, []);
  // Show prompt if the form is fully or partially filled and user wants to navigate away
  const [isDirty, setIsDirty] = React.useState<boolean>(false);
  useEffect(() => {
    // If prompt, when reloading or closing the tab
    window.onbeforeunload = function (e: Event) {
      if (isDirty) {
        e.preventDefault();
        return "Changes you made may not be saved.";
      }
    };
  }, [isDirty]);
  function handleChange(e: any) {
    // Textfield ==>> e.target.value
    // Checkbox / Switch ==>> e.target.checked !== e.target._wrapperState.initialChecked

    formik.handleChange(e);
    e.target.value || JSON.stringify(formik.values) !== JSON.stringify(formik.initialValues)
      ? setIsDirty(true)
      : setIsDirty(false);
  }

  const [isOpenCheckoutDailog, setIsOpenCheckoutDailog] = useState<boolean>(false);

  const handleCloseCheckoutDialog = () => {
    setIsOpenCheckoutDailog(false);
  };

  const handleCheckoutDialog = async () => {
    setIsOpenCheckoutDailog(false);
    formik.submitForm();
  };

  const listener = (e: any) => {
    if (
      (e.key === "s" || e.key === "S") &&
      (navigator.platform.match("Mac") ? e.metaKey : e.ctrlKey)
    ) {
      e.preventDefault();
      setIsOpenCheckoutDailog(true);
    }
  };

  useEffect(() => {
    document.addEventListener("keydown", listener);
    return () => {
      document.removeEventListener("keydown", listener);
    };
  }, []);

  const [isOpenPrintDailog, setIsOpenPrintDailog] = useState<boolean>(false);

  const printReceipt = async (isDuplicate: string) => {
    setIsOpenPrintDailog(false);
    const dateTime = moment().format("DD MMM YYYY, HH:mm");
    const fileMetaData = {
      url: `${process.env.REACT_APP_API_URL}/get-gate-out-slip-by-gate-pass-id/${checkOutDetailObj.gateEntryId}?isDuplicate=${isDuplicate}&printDateTime=${dateTime}`,
      contentType: ".pdf",
      fileName: "testPdf.pdf",
    };
    await dispatch(getPrintFile(fileMetaData, dispatch));
  };

  const [isCustomValidate, setCustomValidate] = useState(false);

  const customValidateHandler = () => {
    // true: pending
    // false: completed

    if (Object.keys(checkoutGateEntryDetailObj).length == 0) {
      return setCustomValidate(true); // pending
    }
    if (checkOutDetailObj.imposeGateoutValidation == false) {
      //   if (checkOutDetailObj?.isDemurrageChargesPaid) {
      return setCustomValidate(false); //completed
      //   } else {
      //     return setCustomValidate(true); //pending
      //   }
    }
    if (checkoutGateEntryDetailObj.consignmentTypeName == "Import") {
      if (
        checkOutDetailObj?.latestSIMO == "HO" ||
        checkOutDetailObj?.latestCRLO == null ||
        checkOutDetailObj?.latestCRLO == "" ||
        checkOutDetailObj?.latestCRLO != "RF" ||
        checkOutDetailObj?.latestCRLO != "BR"
      ) {
        return setCustomValidate(true); //pending
      } else {
        return setCustomValidate(false); //completed
      }
    }
    if (checkoutGateEntryDetailObj.consignmentTypeName == "FCL-Export") {
      if (checkOutDetailObj?.latestSIM == "HO" || checkOutDetailObj?.latestSIM == null) {
        return setCustomValidate(true); //pending
      }
      if (checkOutDetailObj?.latestCRL == "OD" || checkOutDetailObj?.latestCRL == "AL") {
        return setCustomValidate(false); //completed
      }
    }
    if (checkoutGateEntryDetailObj.consignmentTypeName == "Export") {
      if (checkOutDetailObj?.latestOSIM == "HO" || checkOutDetailObj?.latestOSIM == null) {
        return setCustomValidate(true); //pending
      }
      if (checkOutDetailObj?.latestOCRL == "OD" || checkOutDetailObj?.latestOCRL == "AL") {
        return setCustomValidate(false); //completed
      }
    } else {
      return setCustomValidate(false); //completed
    }
  };

  useEffect(() => {
    if (
      checkoutGateEntryDetailObj.consignmentTypeName == "Import" ||
      checkoutGateEntryDetailObj.consignmentTypeName == "Export" ||
      checkoutGateEntryDetailObj.consignmentTypeName == "FCL-Export"
    ) {
      customValidateHandler();
    }
  }, [checkOutDetailObj]);

  return (
    <>
      {/* If prompt, when navigating to another page inside the app */}
      <Prompt when={isDirty} message="Changes you made may not be saved." />
      <Backdrop className={classes.backdrop} open={loader}>
        <CommonLoader />
      </Backdrop>

      {/* Dialog for Print Receipt */}
      <DuplicateReceiptDialog
        isOpen={isOpenPrintDailog}
        handleOriginal={() => {
          printReceipt("0");
        }}
        heading={"Confirmation"}
        handleDuplicate={() => {
          printReceipt("1");
        }}
        onClose={() => {
          setIsOpenPrintDailog(false);
        }}
      >
        Which receipt do you want to print?
      </DuplicateReceiptDialog>

      <Grid item xs={12} lg={10} container>
        {/* Vehilce Number */}
        <Grid item xs={12}>
          <Box>
            <CommonAutoCompleteField
              cssClassName="customTextField"
              label="Vehicle Number"
              placeholder="Enter Vehicle Number"
              name="registrationNumber"
              optionsType={vehicleRegNumberArr}
              getOptionLabel={(option) => option.registrationNumber}
              required
              value={
                vehicleRegNumberArr.find(
                  (st: any) => st.registrationNumber === formik.values.registrationNumber
                ) || null
              }
              onChange={(option: any) => {
                if (option && option.registrationNumber !== "" && option.vehicleId !== 0) {
                  formik.setFieldValue("registrationNumber", option.registrationNumber);
                  handleChange({ target: { value: option.registrationNumber } });
                  handleSearch(option.registrationNumber);
                } else {
                  formik.setFieldValue("registrationNumber", "");
                  handleChange({ target: { value: null } });
                }
              }}
              onKeyUp={(e: any) => {
                setVehicleNumberLength(e.target.value.length);

                if (e.target.value.length == 2 && vehicleNumberLength < 2) {
                  getVehicleByRegistrationNo(e.target.value);
                  return;
                }

                // if (e.keyCode === 13) {
                //   e.target.value ? getVehicleByRegistrationNo(e.target.value) : "";
                //   return;
                // }
              }}
              onKeyDownCapture={(e: any) => {
                if (/[^A-Za-z0-9\d]/.test(e.key)) e.preventDefault();
                else handleChange(e);
              }}
              onPaste={(e: any) => {
                if (/[^A-Za-z0-9\d]/.test(e.clipboardData.getData("text"))) {
                  e.preventDefault();
                  return;
                }

                if (e.clipboardData.getData("text").length >= 2)
                  getVehicleByRegistrationNo(e.clipboardData.getData("text"));
              }}
              error={formik.touched.registrationNumber && Boolean(formik.errors.registrationNumber)}
              helperText={formik.touched.registrationNumber && formik.errors.registrationNumber}
              // InputProps={
              //   <InputAdornment position="start">
              //     <IconButton
              //       aria-label="Search"
              //       onClick={() => {
              //         vehicleNumber ? getVehicleByRegistrationNo(vehicleNumber) : "";
              //       }}
              //       edge="end"
              //       className="icon-button-search"
              //     >
              //       <Search />
              //     </IconButton>
              //   </InputAdornment>
              // }
            />
          </Box>
        </Grid>

        {/* First Status */}
        <Grid xs={12} item container spacing={4} style={{ marginTop: "25px" }}>
          <Grid item xs={7} md={6} className={classes.noPadding}>
            <Typography variant="h6" className={classes.subHeadings}>
              Vehicle Scanning
            </Typography>
          </Grid>
          <Grid item xs={5} md={6} className={classes.noPadding}>
            {checkOutDetailObj.isAnomlyDetected === true ? (
              <StatusCancel title="Anomaly Detected" />
            ) : checkOutDetailObj.isScanComplete === true ? (
              <StatusCompleted />
            ) : (
              <StatusPending />
            )}
          </Grid>
          <Divider className={classes.cusDivider} />
        </Grid>
        {/* Second Status */}
        <Grid xs={12} item container spacing={4} style={{ marginTop: "25px" }}>
          <Grid item xs={7} md={6} className={classes.noPadding}>
            <Typography variant="h6" className={classes.subHeadings}>
              Vehicle Weight
            </Typography>
          </Grid>
          <Grid item xs={5} md={6} className={classes.noPadding}>
            {checkOutDetailObj?.isWeightDone ? <StatusCompleted /> : <StatusPending />}
          </Grid>
          <Divider className={classes.cusDivider} />
        </Grid>
        {/* Third Status */}
        <Grid xs={12} item container spacing={4} style={{ marginTop: "25px" }}>
          <Grid item xs={7} md={6} className={classes.noPadding}>
            <Typography variant="h6" className={classes.subHeadings}>
              Commerical Payment
            </Typography>
          </Grid>
          <Grid item xs={5} md={6} className={classes.noPadding}>
            {checkOutDetailObj?.isPaymentCompleted ? (
              <StatusCompleted />
            ) : (
              <Grid container direction="row">
                <StatusPending />
                <Box
                  className={classes.subStatusPending}
                  paddingLeft="4px"
                  paddingTop="4px"
                  fontWeight="bold"
                >
                  {" "}
                  {checkOutDetailObj?.amount
                    ? `(PKR ${(
                        checkOutDetailObj?.amount - checkOutDetailObj?.amountPaid
                      ).toLocaleString(undefined, { maximumFractionDigits: 2 })})`
                    : ""}
                </Box>
              </Grid>
            )}
          </Grid>
          <Divider className={classes.cusDivider} />
        </Grid>
        {/* Fourth Status */}
        {checkoutGateEntryDetailObj.consignmentTypeName == "Import" ||
        checkoutGateEntryDetailObj.consignmentTypeName == "Export" ||
        checkoutGateEntryDetailObj.consignmentTypeName == "FCL-Export" ? (
          <Grid xs={12} item container spacing={4} style={{ marginTop: "25px" }}>
            <Grid item xs={7} md={6} className={classes.noPadding}>
              <Typography variant="h6" className={`${classes.subHeadings}`}>
                Custom Clearance
              </Typography>
            </Grid>
            <Grid item xs={5} md={6} className={classes.noPadding}>
              {isCustomValidate ? <StatusPending /> : <StatusCompleted />}
            </Grid>
            <Divider className={classes.cusDivider} />
          </Grid>
        ) : (
          <></>
        )}

        <Grid item xs={12} style={{ marginTop: "15px" }}>
          <Box>
            <TextField
              className="customTextField"
              size="small"
              fullWidth
              variant="outlined"
              InputLabelProps={{
                shrink: true,
              }}
              label="Remarks"
              name="remarks"
              disabled={false}
              multiline
              rows={4}
              value={formik.values.remarks}
              onChange={handleChange}
              error={formik.touched.remarks && Boolean(formik.errors.remarks)}
              helperText={formik.touched.remarks && formik.errors.remarks}
            />
          </Box>
        </Grid>
        <Grid item container xs={12}>
          <CommonButton
            isGreen
            cssClass={classes.gateOutButton}
            onClick={() => {
              setIsOpenCheckoutDailog(true);
            }}
          >
            Checkout Vehicle
          </CommonButton>

          <CommonButton
            cssClass={classes.gateOutButton}
            style={{ marginLeft: "10px" }}
            onClick={() => {
              checkOutDetailObj.gateEntryId ? setIsOpenPrintDailog(true) : "";
            }}
          >
            Print
          </CommonButton>
        </Grid>
      </Grid>

      <TransitionDialog
        isOpen={isOpenCheckoutDailog}
        handleClose={handleCloseCheckoutDialog}
        heading={isCustomValidate ? "Error" : "Confirmation"}
        handleConfirm={handleCheckoutDialog}
        isCustomValidate={isCustomValidate}
      >
        {isCustomValidate
          ? "You can't checkout this vehicle, because custom clearance is pending."
          : "Are you sure you want to checkout this vehicle?"}{" "}
        <br />
        {!isCustomValidate &&
          checkOutDetailObj &&
          checkOutDetailObj?.isDemurrageChargesPaid == false &&
          "Demurrage charges are not paid."}
      </TransitionDialog>
    </>
  );
}
